// static/js/components/SidebarChatMount.jsx

/**
 * SidebarChatMount Component
 *
 * This React component serves as the sidebar for managing chat threads within the application.
 * It handles the creation and display of chat threads, provides UI for selecting active threads,
 * and triggers refreshes when new chats are created. It interfaces with other parts of the chat UI
 * to coordinate the application's behavior through events.
 *
 * Features:
 * - Manages the state of the current and newly created chat threads.
 * - Uses React hooks such as `useState` and `useEffect` for state management and lifecycle events.
 * - Dispatches custom window events to notify other components of changes, such as when a new chat
 *   is created or a thread is selected.
 * - Listens for global events that indicate the creation of a new chat and responds by refreshing
 *   the chat thread list.
 * - Extracts CSRF token from meta tags for secure requests (if necessary).
 * - Ensures that the component is mounted only when the DOM is fully loaded, and mounts the
 *   `ChatThreadList` component which actualizes the rendering of chat threads.
 *
 * This component is designed to be a standalone mount point for the sidebar portion of the chat
 * application, ensuring isolation and modular management of chat threads.
 */

import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import ChatThreadList from './ChatThreadList';

const SidebarChatMount = () => {
  // Track both selected thread and refresh state
  const [currentThreadId, setCurrentThreadId] = useState(null);
  const [shouldRefresh, setShouldRefresh] = useState(false);  // explicit refresh state

  // Get CSRF token from meta tag
  const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');

  const handleThreadSelect = async (threadId) => {
    try {
      // Notify the main chat interface about thread switch
      window.dispatchEvent(new CustomEvent('threadSwitch', {
        detail: { threadId }
      }));
    } catch (error) {
      console.error('Error switching thread:', error);
    }
  };

  // Trigger refresh after successful creation
  const handleNewChat = () => {
    // Notify the main chat interface to create new chat
    window.dispatchEvent(new CustomEvent('newChat'));
    // Trigger refresh of thread list
    setShouldRefresh(true);  // trigger refresh when new chat is created
  };

  // Listen for chat creation events from main chat interface
  useEffect(() => {
    const handleChatCreated = () => {
      setShouldRefresh(true);
    };

    window.addEventListener('chatCreated', handleChatCreated);

    // Reset refresh trigger after ChatThreadList processes it
    if (shouldRefresh) {
      setTimeout(() => setShouldRefresh(false), 100);
    }

    return () => {
      window.removeEventListener('chatCreated', handleChatCreated);
    };
  }, [shouldRefresh]);

  return (
    <ChatThreadList
      onThreadSelect={handleThreadSelect}
      onNewChat={handleNewChat}
      csrfToken={csrfToken}
      shouldRefresh={shouldRefresh}  // pass refresh trigger to ChatThreadList
    />
  );
};

// Mount the component when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('chat-thread-list-root');
  if (container) {
    const root = createRoot(container);
    root.render(<SidebarChatMount />);
  }
});

export default SidebarChatMount;
